import { useEffect, useState } from 'react';

import { getPopular } from './PopularItems';
import { cars } from '../constants';

import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';


/**
 * Custom hook for filtering, sorting, and paginating cars.
 */
export const useCarFilter = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const { search } = useParams();
  const [searchParams] = useSearchParams();

  // State management
  const [matchingCars, setMatchingCars] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  // Set items per page based on route
  const itemsPerPage = location.pathname.toLowerCase().includes('list') ? 4 : 6;

  // Update search keyword from URL param
  useEffect(() => {
    setSearchKeyword(search || '');
  }, [search]);

  // Filtering and sorting logic triggered by URL changes or user actions
  useEffect(() => {
    const carMake = searchParams.get('carMake') || '';
    const carModel = searchParams.get('carModel') || '';
    const carType = searchParams.get('carType') || '';
    const carYear = searchParams.get('carYear') || '';
    const transmission = searchParams.get('transmission') || '';
    const carFuelType = searchParams.get('carFuelType') || '';
    const maxPrice = searchParams.get('maxPrice') || '';

    // Apply form-based filtering
    let filteredCars = handleSearch(
      carMake,
      carModel,
      carType,
      carYear,
      transmission,
      carFuelType,
      maxPrice,
      cars
    );

    // Apply keyword search (if present)
    if (searchKeyword) {
      filteredCars = handleHeaderSearch(searchKeyword);
    }

    // Apply sorting
    filteredCars = handleSortCars(filteredCars, sortOption);

    // Update matching cars
    setMatchingCars(filteredCars);
    setCurrentPage(1);  
  }, [searchParams, searchKeyword, sortOption]);

  // Sort cars based on selected option
  const handleSortCars = (cars, sortOption) => {
    switch (sortOption) {
      case 'featured': return getPopular(cars);
      case 'latest': return [...cars].sort((a, b) => b.details.year - a.details.year);
      case 'lowPrice': return [...cars].sort((a, b) => a.price - b.price);
      case 'highPrice': return [...cars].sort((a, b) => b.price - a.price);
      default: return cars;
    }
  };

  // Filter cars based on form fields
  const handleSearch = (
    carMake,
    carModel,
    carType,
    carYear,
    transmission,
    carFuelType,
    maxPrice,
    cars
  ) => {
    return cars.filter((car) => {
      const parsedMaxPrice = maxPrice ? Number(maxPrice) : Infinity;
      const parsedCarYear = carYear ? parseInt(carYear, 10) : null;

      return (
        (carMake === '' || car.details.carMake.toLowerCase().includes(carMake.toLowerCase())) &&
        (carModel === '' || car.details.carModel.toLowerCase().includes(carModel.toLowerCase())) &&
        (carType === '' || car.details.carType.toLowerCase().includes(carType.toLowerCase())) &&
        (!parsedCarYear || car.details.year === parsedCarYear) &&
        (transmission === '' || car.details.transmission.toLowerCase() === transmission.toLowerCase()) &&
        (carFuelType === '' || car.details.fuelType.toLowerCase() === carFuelType.toLowerCase()) &&
        car.price <= parsedMaxPrice
      );
    });
  };

  // Search via global keyword (from header)
  const handleHeaderSearch = (searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    return cars.filter((item) => (
      String(item.details.year).toLowerCase().includes(searchLower) || 
      item.details.carMake?.toLowerCase().includes(searchLower) ||
      item.details.carModel?.toLowerCase().includes(searchLower) ||
      item.details.carType?.toLowerCase().includes(searchLower) ||
      item.details.fuelType?.toLowerCase().includes(searchLower) ||
      item.details.transmission?.toLowerCase().includes(searchLower)
    ));
  };

  // Form submission handler: updates URL with selected filters
  const handleFormSubmit = (
    carMake,
    carModel,
    carType,
    carYear,
    transmission,
    carFuelType,
    maxPrice
  ) => {
    const params = new URLSearchParams();
    if (carMake) params.set('carMake', carMake);
    if (carModel) params.set('carModel', carModel);
    if (carType) params.set('carType', carType);
    if (carYear) params.set('carYear', carYear);
    if (transmission) params.set('transmission', transmission);
    if (carFuelType) params.set('carFuelType', carFuelType);
    if (maxPrice) params.set('maxPrice', maxPrice);

    navigate(`/Cars?${params.toString()}`);
    setCurrentPage(1);
  };

  // Header search submit handler: navigates to search page
  const handleHeaderSearchSubmit = (selectedSearchQuery) => {
    const encodedQuery = encodeURIComponent(selectedSearchQuery);
    navigate(`/Cars/search/${encodedQuery}`);
  };

  // Sort option change handler
  const handleSort = (selectedOption) => {
    setSortOption(selectedOption ? selectedOption.value : '');
    setCurrentPage(1);
  };

  // Pagination logic
  const displayCars = matchingCars.length > 0 ? matchingCars : cars;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedCars = displayCars.slice(startIndex, endIndex);
  const totalPages = Math.ceil(displayCars.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    searchKeyword,
    matchingCars,
    sortOption,
    currentPage,
    displayedCars,
    handleFormSubmit,
    handleSort,
    totalPages,
    handlePageChange,
    handleHeaderSearchSubmit
  };
};

// ================================
// Extract Dynamic Counts for Filtering
// ================================

const carMakeCounts = {};
const carModelsByMake = {}; 
const carTypeCounts = {};
const carFuelCounts = {};
const carTransmissionCounts = {};

// Loop through car list and extract counts for filter dropdowns
cars.forEach((car) => {
  const make = car.details.carMake.toLowerCase();
  const model = car.details.carModel.toLowerCase();
  const type = car.details.carType.toLowerCase();
  const fuel = car.details.fuelType.toLowerCase();
  const transmission = car.details.transmission.toLowerCase();

  carMakeCounts[make] = (carMakeCounts[make] || 0) + 1;
  carTypeCounts[type] = (carTypeCounts[type] || 0) + 1;
  carFuelCounts[fuel] = (carFuelCounts[fuel] || 0) + 1;
  carTransmissionCounts[transmission] = (carTransmissionCounts[transmission] || 0) + 1;

  if (!carModelsByMake[make]) {
    carModelsByMake[make] = new Set();
  }
  carModelsByMake[make].add(model);
});

// ================================
// Generate Dropdown Options
// ================================

export const carMakes = [
  { value: '', label: 'Car Make' },
  ...Object.keys(carMakeCounts).map((make) => ({
    value: make,
    label: make.charAt(0).toUpperCase() + make.slice(1),
  })),
];

export const carTypes = [
  { value: '', label: 'Car Type' },
  ...Object.keys(carTypeCounts).map((type) => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  })),
];

export const carYears = [
  { value: '', label: 'Select Year' },
  ...Array.from({ length: 36 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  }),
];

export const carTransmissions = [
  { value: '', label: 'Transmission' },
  ...Object.keys(carTransmissionCounts).map((transmission) => ({
    value: transmission,
    label: transmission.charAt(0).toUpperCase() + transmission.slice(1),
  })),
];

export const carFuelTypes = [
  { value: '', label: 'Fuel Type' },
  ...Object.keys(carFuelCounts).map((fuel) => ({
    value: fuel,
    label: fuel.charAt(0).toUpperCase() + fuel.slice(1),
  })),
];

// ================================
// Utility Functions
// ================================

// Get available models for selected car make
export const getCarModels = (carMake) => {
  return carMake && carModelsByMake[carMake]
    ? [
        { value: '', label: 'Car Model' },
        ...Array.from(carModelsByMake[carMake]).map((model) => ({
          value: model,
          label: model.charAt(0).toUpperCase() + model.slice(1),
        })),
      ]
    : [{ value: '', label: 'Select a Make First', isDisabled: true }];
};

// Extract search params from URL
export const extractSearchParams = (searchParams) => {
  return {
    carMake: searchParams.get('carMake') || '',
    carModel: searchParams.get('carModel') || '',
    carType: searchParams.get('carType') || '',
    carYear: searchParams.get('carYear') || '',
    transmission: searchParams.get('transmission') || '',
    carFuelType: searchParams.get('carFuelType') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  };
};

// Generic change handler for select inputs
export const handleSelectChange = (setter) => (selectedOption) => {
  setter(selectedOption ? selectedOption.value : '');
};

// Resets all form fields
export const resetFormFields = (setters) => {
  setters.forEach((setter) => setter(''));
};